import { DateTime } from "luxon";
import { AvaibilityType, BasketItem, BasketItemDTO } from "../../explorer/types/Basket/BasketItems";
import { OfferDetailPart } from "../../explorer/types/Basket/OfferDetailPart";
import {
    OrderFromOfferDTO,
    OfferDetailsResponse,
} from "../../explorer/types/Basket/OfferDetailsResponse";
import { OrderDetailPart } from "../../explorer/types/Basket/OrderDetailPart";
import { OrderDetailsResponse } from "../../explorer/types/Basket/OrderDetailsResponse";
import useCurrentSite from "./useCurrentSite";
import { usePartFormatter } from "./usePartFormatter";
import { useReduxState } from "../../explorer/redux/hooks";
import { Site } from "../../explorer/types/SpareParts";
import { reduxStoreConfig } from "../configuration/reduxStoreConfig";
import useBrands from "../../explorer/hooks/useBrands";
import useLanguages from "../../explorer/hooks/useLanguages";
import ServiceProvider from "../configuration/ServiceProvider";
import { AvailabilityPartRequestDTO, AvailabilityPartResponseDTO } from "../../explorer/types/Basket/AvailabilityRequest";

export const useTrackingOrderOffer: () => {
    getPropsForOrder: (
        details: OfferDetailsResponse | OrderDetailsResponse
    ) => OrderFromOfferDTO;

    mapDetailsToOrderPrices: (
        details: OfferDetailsResponse | OrderDetailsResponse
    ) => {};

    mapPartsToBasketItems: (
        parts: (OfferDetailPart | OrderDetailPart)[],
        basketId: number
    ) => BasketItemDTO[];

    mapPartsToOrderItems: (
        parts: (OfferDetailPart | OrderDetailPart)[],
        avaibilityList: AvailabilityPartResponseDTO[]
    ) => BasketItem[];

    mapPartsToAvailabilityItems: (
        parts: (OfferDetailPart | OrderDetailPart)[]
    ) => AvailabilityPartRequestDTO[];

    isOffer: (value: number) => boolean;
    isOrderable: (
        details: OfferDetailsResponse | OrderDetailsResponse | undefined
    ) => boolean;

    getCustomerSiteNameByCode: (csc: string) => string;
    getCustomerAddressByCode: (csc: string) => string;
    getSiteByCode: (code: string) => Site;
} = () => {
    const [currentSite, setCurrentSite] = useCurrentSite();
    const partFormatter = usePartFormatter();
    const [sites, setSites] = useReduxState<Site[]>(
        reduxStoreConfig.sparePartsSites
    );
    const isOffer: (value: number) => boolean = (value: number) => {
        return value === 1;
    };

    const brand = useBrands().currentBrandUrl;
    const currentLanguage = useLanguages().currentLanguage;

    function isOrderable(
        details: OfferDetailsResponse | OrderDetailsResponse | undefined
    ): details is OfferDetailsResponse {
        const ofd: OfferDetailsResponse = details as OfferDetailsResponse;
        const nowDate = DateTime.now().toFormat("yyyyLLdd").toString();

        if (!ofd) return false;

        return (
            ofd.offerStatus === "PROCESSED"  && +ofd.offerExpirationDate  > +nowDate
        );
    }

    function isOfferDetailPart(
        part: OfferDetailPart | OrderDetailPart
    ): part is OfferDetailPart {
        return (part as OfferDetailPart).requestedQuantity !== undefined; // Verifica che l'oggetto sia un OfferDetailPart
    }
    function idOfferDetail(
        part: OfferDetailsResponse | OrderDetailsResponse
    ): part is OfferDetailsResponse {
        return (part as OfferDetailsResponse).offerCode !== undefined; // Verifica che l'oggetto sia un OfferDetailPart
    }
    const mapPartsToBasketItems = (
        parts: (OfferDetailPart | OrderDetailPart)[],
        basketId: number
    ): BasketItemDTO[] => {
        return parts
        // .filter((row) => {
        //     return row.showBasket === null || row.showBasket === true;
        // })
        .map((row) => {
            if (isOfferDetailPart(row)) {
                return {
                    code: row.itemCode,
                    qty: row.requestedQuantity,
                    basketId: basketId,
                    navContext: partFormatter.setNavContextByData(
                        row.machineSN,
                        row.table,
                        row.position
                    ),
                    manufacturerCompanyCode: row.manufacturerCompany,
                };
            } else {
                return {
                    code: row.itemCode,
                    qty: row.deliveredQuantity || row.orderedQuantity,
                    basketId: basketId,
                    navContext: partFormatter.setNavContextByData(
                        row.machineSN,
                        row.table,
                        row.position
                    ),
                    manufacturerCompanyCode: row.manufacturerCompany,
                };
            }
        });
    };

    const mapPartsToAvailabilityItems = (
        parts: (OfferDetailPart | OrderDetailPart)[]
    ): AvailabilityPartRequestDTO[] => {
        return parts
            .filter((row) => row !== undefined && isOfferDetailPart(row))
            .map((row) => {
                return {
                    partCode: row.itemCode,
                    quantity: isOfferDetailPart(row)
                        ? "" + row.requestedQuantity
                        : "0",
                    manufacturerCompany: row.manufacturerCompany,
                };
            });
    };

    const mapPartsToOrderItems = (
        parts: (OfferDetailPart | OrderDetailPart)[],
        avaibilityList: any
    ): BasketItem[] => {
        const partMap = new Map<string, string>(
            avaibilityList.parts.map((part: AvailabilityPartResponseDTO) => [
                part.partCode,
                part.status,
            ])
        );
        return parts
            .filter((row) => row !== undefined && isOfferDetailPart(row))
            .map((row) => {
                return {
                    code: row.itemCode,
                    manufacturerCompanyCode: row.manufacturerCompany,
                    partDescription: row.itemDescription,
                    customDescription: "",
                    qty: isOfferDetailPart(row) ? row.requestedQuantity : 0,
                    basketId: "",
                    navContext: partFormatter.setNavContextByData(
                        row.machineSN,
                        row.table,
                        row.position
                    ),

                    warningMinQty: false,
                    warningPackageQty: false,
                    minOrderQty: isOfferDetailPart(row)
                        ? row.requestedQuantity
                        : 0,
                    replacedCode: "",

                    hidePrice: false,
                    images: [],

                    currency: isOfferDetailPart(row) ? row.currency : "EUR",
                    discount: "" + row.discountPercentage,
                    netTotalPrice: row.netTotalPrice,
                    grossUnitPrice: row.grossUnitPrice,

                    rowId: isOfferDetailPart(row) ? row.rowId : "",
                    uom: row.unitOfMeasure,
                    note: row.note,
                    availabilityStatus: mapStatusToAvailabilityType(partMap.get(row.itemCode) || "none"),
                };
            });
    };

    function mapStatusToAvailabilityType(status: string): AvaibilityType {
        switch (status) {
            case "available":
                return AvaibilityType.Green;
            case "planned":
                return AvaibilityType.Yellow;
            case "partially available":
                return AvaibilityType.Orange;
            case "not available":
                return AvaibilityType.Red;
            default:
                return AvaibilityType.Grey;
        }
    }

    const mapDetailsToOrderPrices = (
        details: OfferDetailsResponse | OrderDetailsResponse
    ): {} => {
        return {
            fullTotal: details.grossTotalAmount,
            netTotal: details.netTotalAmount,
            discount: details.discountAmount,
            hidePrice: false,
        };
    };
    const getPropsForOrder = (
        details: OfferDetailsResponse | OrderDetailsResponse
    ): OrderFromOfferDTO => {
        return {
            billTo: {
                billToCode: details.billTo,
                billToDto: details.billToDto,
            },
            shipTo: {
                shipToCode: details.shipTo,
                shipToDto: details.shipToDto,
            },
            customerReference: details.customerReference,
            note: details.note,
            offerCode: (details as OfferDetailsResponse).offerCode,
            customerOfferCode: (details as OfferDetailsResponse)
                .customerOfferCode,
            sellingCompanyCode: brand.toUpperCase(),
            currentLanguage: currentLanguage,
            customerCode: currentSite.customerCode ?? "",
        };
    };

    const getCustomerSiteNameByCode = (code: string): string => {
        const site = sites.find((site) => site.customerSiteCode === code);
        return site?.customerSiteName ?? "-";
    };

    const getCustomerAddressByCode = (code: string): string => {
        if (code === "") return "-";

        const site = sites.find((site) => site.customerSiteCode === code);
        return `${site?.address} ${site?.address && "-"} ${site?.city}, ${
            site?.zipCode
        } (${site?.country})`;
    };

    const getSiteByCode = (code: string): Site => {
        if (code === "") return {};
        return sites.find((site) => site.customerSiteCode === code) ?? {};
    };

    return {
        getPropsForOrder,
        mapDetailsToOrderPrices,
        mapPartsToOrderItems,
        mapPartsToBasketItems,
        mapPartsToAvailabilityItems,
        isOffer,
        isOrderable,
        getCustomerSiteNameByCode,
        getCustomerAddressByCode,
        getSiteByCode,
    };
};